"use client";

import { useState, useEffect, JSX } from "react";
import { Bookmark } from "lucide-react";
import { useUser } from "@/context/user-context";
import { toast } from "sonner";

interface BookmarkTagProps {
  articleId: { documentId: string };
}

export default function BookmarkTag({
  articleId,
}: BookmarkTagProps): JSX.Element | null {
  const { token, user } = useUser();
  const [bookmarked, setBookmarked] = useState<any>();
  const [bookmarkedArticles, setBookmarkedArticles] = useState<any>();

  useEffect(() => {
    if (!articleId?.documentId) return;
    const bookmarks = JSON.parse(localStorage.getItem("bookmarks") || "{}");
    const alreadyBooked = Object.values(bookmarks);
    setBookmarkedArticles(alreadyBooked);
    setBookmarked(bookmarks);
  }, [articleId]);

  const toggleBookmark = async () => {
    if (window === undefined) {
      return;
    }
    if (!token) {
      window.location.href = "/login?redirect=/profile";
      return;
    }

    if (!articleId?.documentId) return;

    const bookmarks = bookmarked;

    const alreadyBooked = bookmarkedArticles?.find(
      (a) => a === articleId?.documentId
    );

    try {
      // Update local storage
      if (alreadyBooked) {
        const keyOfValue = Object.keys(bookmarks).find(
          (key) => bookmarks[key] === articleId.documentId
        );
        if (keyOfValue) {
          const res = await fetch(
            `${process.env.NEXT_PUBLIC_MEDIA_BASE_URL}/api/bookmarks/${keyOfValue}`,
            {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (res.status === 204) {
            delete bookmarks[keyOfValue];
            console.log(keyOfValue, bookmarks);

            localStorage.setItem("bookmarks", JSON.stringify(bookmarks));
            setBookmarkedArticles((p) =>
              p.filter((id) => id !== articleId.documentId)
            );
            setBookmarked(bookmarks);
          }
        }
      } else {
        // Sync with server
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_MEDIA_BASE_URL}/api/bookmarks`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              data: {
                user: user?.documentId,
                article: articleId.documentId,
              },
            }),
          }
        );

        const resData = await res.json();
        if (resData?.data?.documentId) {
          bookmarks[resData?.data?.documentId] = articleId?.documentId;
          localStorage.setItem("bookmarks", JSON.stringify(bookmarks));
          setBookmarkedArticles((p) => [...p, articleId?.documentId]);
          setBookmarked(bookmarks);
        }
      }
      // Notify UserProfilePage of bookmark change
      window.dispatchEvent(new Event("bookmarkChange"));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Error syncing bookmark:", error);
      }
      toast.error("Failed to update bookmark");
    }
  };

  if (!articleId?.documentId) return null;

  if (!token) {
    return (
      <button
        disabled
        className="p-2 rounded-full bg-neutral-100 text-neutral-600 opacity-50 relative group"
        aria-label="Log in to bookmark"
      >
        <Bookmark className="w-5 h-5" />
        <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-2 py-1 text-xs bg-black text-white rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 whitespace-nowrap">
          Login to your account, to add article as bookmark
        </span>
      </button>
    );
  }
  const alreadyBooked = bookmarkedArticles?.find(
    (a) => a === articleId?.documentId
  );
  return (
    <button
      onClick={toggleBookmark}
      className={`p-2 rounded-full transition-colors ${
        alreadyBooked
          ? "bg-neutral-100 text-neutral-700"
          : "bg-neutral-100 text-neutral-600"
      } hover:bg-neutral-200 focus:outline-none focus:ring-2 focus:ring-neutral-400`}
      aria-label={alreadyBooked ? "Remove bookmark" : "Add bookmark"}
    >
      <Bookmark className={`w-5 h-5 ${alreadyBooked ? "fill-current" : ""}`} />
    </button>
  );
}
